import { sectionRegistry } from './sectionRegistry';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bt, btp } from '../i18n';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  EditableSection,
  FallbackSection,
  FULL_BLEED_OPENERS,
  resolveSettings,
  SectionErrorBoundary,
  type RenderContext,
  type SectionInstance,
} from '@aquilixshop/ui-react';
import { isReadOnlyView } from '../boot/view-handoff';
import { useBuilderStore } from '../state/builderStore';
import { canvasBuyBoxJourneys, useStoreIsInCartMode } from '../state/cartMode';
import type { PreviewReviewsFailure } from '../lib/catalogApi';
import { CHROME_SECTION_TYPES } from '../state/sectionZones';
import { useGenerationStore } from '../state/generation/generationStore';
import { sectionDisplayName } from '../shell/sectionVariantLabel';
import { EyeIcon, EyeOffIcon, TrashIcon } from '../ui/icons';
import { resolveCanvasTarget } from './canvasTarget';

/**
 * The two sentences the reviews band prints when its fetch did NOT come back: what happened, and what
 * she can do about it (the same shape the import banners carry — a failure that only says "failed" is a
 * failure she has to guess at).
 *
 * The fix line points at CLICKING THE SECTION, because that is a real retry: selecting a
 * `product-reviews` section re-asks, since a failed answer is never cached.
 */
function reviewsFailureText(reason: PreviewReviewsFailure): { why: string; fix: string } {
  if (reason === 'session') {
    return {
      why: bt(
        'chrome.reviews.fail.session.why',
        'Reviews could not load: your session has expired.',
      ),
      fix: bt(
        'chrome.reviews.fail.session.fix',
        'Sign in again on the dashboard, then reload the editor.',
      ),
    };
  }
  if (reason === 'offline') {
    return {
      why: bt(
        'chrome.reviews.fail.offline.why',
        'Reviews could not load: the server did not answer.',
      ),
      fix: bt(
        'chrome.reviews.fail.offline.fix',
        'Check your connection, then click this section to try again.',
      ),
    };
  }
  return {
    why: bt(
      'chrome.reviews.fail.server.why',
      'Reviews could not load: the server returned an error.',
    ),
    fix: bt(
      'chrome.reviews.fail.server.fix',
      'Click this section to try again; if it keeps failing, reload the editor.',
    ),
  };
}

/**
 * The granular per-section render path (build-plan §4.1–§4.2, §9.1).
 *
 * Each `MemoSection` subscribes to ONLY its own slice of the store (`sectionsById[id]`, plus the
 * selected flag and the stable `renderContext`), so a settings edit re-renders exactly one section —
 * NOT the whole-doc `hydrateDocument`-per-render of raw `PageRenderer`. The components are the IDENTICAL
 * registry components the storefront paints, wrapped only in the editor-unaware `EditableSection`
 * chrome — same React tree, no iframe, no postMessage.
 *
 * `SectionInner` is `React.memo`'d: it re-renders only when `section` or `context` changes by
 * reference. Immer's structural sharing keeps untouched sections referentially equal, so they bail out
 * for free during a drag (which reorders `order[]` only; `sectionsById` refs are untouched).
 *
 * M3.3 wires the outer node into dnd-kit's `useSortable` (keyed by the stable section id, never the
 * array index) — the `dragHandle` attrs/listeners flow through `EditableSection`'s handle button so
 * reorder is mouse- AND keyboard-operable.
 */
const SectionInner = React.memo(function SectionInner({
  section,
  context,
}: {
  section: SectionInstance;
  context: RenderContext;
}) {
  const def = sectionRegistry[section.type];
  if (!def) {
    // Unknown section type (document authored against a newer registry) → labelled placeholder.
    return <FallbackSection type={section.type} mode="edit" />;
  }
  const Component = def.Component;
  return (
    <SectionErrorBoundary mode="edit">
      <Component section={section} context={context} />
    </SectionErrorBoundary>
  );
});

/**
 * Per-section hydration memo (build-plan §9.4): resolve the instance's settings (migrate + defaults)
 * once per section reference, NOT the whole document per keystroke.
 */
function useHydratedSection(section: SectionInstance): SectionInstance {
  return useMemo(() => {
    const def = sectionRegistry[section.type];
    if (!def) return section;
    return {
      ...section,
      settings: resolveSettings(def.schema, section),
      blocks: section.blocks ?? [],
    };
  }, [section]);
}

export function MemoSection({ id, index }: { id: string; index: number }) {
  const section = useBuilderStore((s) => s.sectionsById[id]);
  const selected = useBuilderStore((s) => s.selectedSectionId === id);
  const rawContext = useBuilderStore((s) => s.renderContext);
  // During a generation (and the embedded preview after it) the DEMO CATALOG is unreachable: the
  // canvas renders as the authoritative storefront does — an unbound picker section hides instead of
  // painting the fake «BERQ» shelf (founder 2026-08-21: «j'ai pas ces produits»).
  const suppressDemo = useGenerationStore(
    (s) => (s.mode === 'takeover' && s.status !== 'idle') || s.embeddedPreview,
  );
  // The bound product's REAL reviews (feature 0066), merged in HERE rather than threaded through
  // `buildEditContext`: that context is rebuilt from a dozen places and a value passed through all of them
  // is a value that goes missing from one. Merged at render time, the canvas shows the shop's actual
  // numbers — it used to carry none at all, so `product-reviews` printed "0 reviews" on every store and a
  // seller who had just imported a file concluded the import had failed.
  const previewReviews = useBuilderStore((s) => s.previewReviews);
  // ...and whether that answer is simply not back yet. Without it, `product-reviews` reads its empty
  // `reviews` as "this shop has none" and prints "0 reviews" — a false statement about the shop, held for
  // the whole round trip, on a link where that is seconds (founder, 16/09/2026).
  const previewReviewsLoading = useBuilderStore((s) => s.previewReviewsLoading);
  // ...and, when the answer is not coming at all, WHY. Three causes, each with its own fix, translated
  // HERE (builder dictionary) rather than keyed into the 26 storefront dictionaries: no visitor can ever
  // read this string, because a storefront render never sets it.
  const previewReviewsError = useBuilderStore((s) => s.previewReviewsError);
  // Can a shopper of this STORE end up in a cart? (the header's cart icon — see state/cartMode.ts)
  const cartMode = useStoreIsInCartMode();
  // Does the page BODY open on a full-bleed visual? — what tells the header whether to float over that
  // visual or wear its normal bar (see ui-react `renderer/opensOnMedia.ts`). Merged here for the same
  // reason `previewReviews` is, and because it CHANGES WHILE SHE EDITS: adding a hero, hiding it, dragging
  // it down — the canvas has to answer differently each time, which a context built once on load cannot.
  // A primitive, so the selector only re-renders sections when the answer actually flips.
  //
  // The editor's list is the COMPOSED one (chrome + body + footer), so the chrome types are skipped: the
  // first section of the list is the header itself, never the page's opener.
  // The id of the section that OPENS the page — the first one that is not chrome. On the storefront the
  // body sections are the direct children of their own page root, so `:first-child` finds it; here every
  // section is wrapped and the chrome shares the list, so the editor has to name it. The header's own CSS
  // reads the attribute to flush its top padding while the bar floats over it.
  const openerId = useBuilderStore(
    (s) =>
      s.order.find((sid) => {
        const sec = s.sectionsById[sid];
        return !!sec && !sec.disabled && !CHROME_SECTION_TYPES.includes(sec.type);
      }) ?? null,
  );
  const opensOnMedia = useBuilderStore((s) => {
    const first = openerId ? s.sectionsById[openerId] : undefined;
    return !!first && FULL_BLEED_OPENERS.includes(first.type);
  });
  const context = useMemo(() => {
    if (!rawContext) return rawContext;
    const failure = previewReviewsError ? reviewsFailureText(previewReviewsError) : undefined;
    const withReviews = previewReviews
      ? { ...rawContext, reviews: previewReviews, reviewsLoading: previewReviewsLoading }
      : { ...rawContext, reviewsLoading: previewReviewsLoading, reviewsError: failure };
    const withMedia = { ...withReviews, pageOpensOnMedia: opensOnMedia };
    // Cart mode, STORE-wide (other pages + this one, live) — what the header reads to keep or drop the cart
    // icon, merged here for the same reason as the reviews: the canvas must paint exactly what the
    // inspector's switch says (locked ON, or the seller's choice), and a journey flipped on this page must
    // move the icon at once. See `canvasBuyBoxJourneys` for why a boolean is enough here.
    const withJourneys = { ...withMedia, buyBoxJourneys: canvasBuyBoxJourneys(cartMode) };
    return suppressDemo ? { ...withJourneys, catalogResolved: true } : withJourneys;
  }, [
    rawContext,
    suppressDemo,
    previewReviews,
    previewReviewsLoading,
    previewReviewsError,
    opensOnMedia,
    cartMode,
  ]);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const toggleSectionDisabled = useBuilderStore((s) => s.toggleSectionDisabled);
  const removeSection = useBuilderStore((s) => s.removeSection);
  const selectMode = useBuilderStore((s) => s.selectMode);
  const selectBlock = useBuilderStore((s) => s.selectBlock);
  const focusField = useBuilderStore((s) => s.focusField);

  // A HIDDEN section paints nothing (see below), so it must not take part in the canvas drag either:
  // as a zero-height DROP TARGET it stole the drop from its visible neighbour, and since neither the
  // ghost nor the canvas moved a pixel, the reorder was invisible — that's how a hidden header silently
  // sank into the middle of the page (bug 2026-08-03). `disabled` unregisters it as both draggable and
  // droppable while keeping its slot in `order[]`.
  const hidden = !!section?.disabled;
  // Superadmin consultation: a consulted page is not draggable either — a drop reorders the document,
  // and the rail's own rows were already frozen (founder 25/10/2026).
  const readOnly = isReadOnlyView();
  const sortable = useSortable({ id, disabled: hidden || readOnly });

  // The floating toolbar is inset at the section's TOP-RIGHT. A tall section has room to spare; a SHORT
  // one — a header bar (~61px), an announcement strip (~41px) — is barely taller than the toolbar, so it
  // landed inside the bar and covered the very content you were trying to click. On the 390px mobile
  // canvas that is most of the header: the logo and every icon sat under "Ask for changes". Measure the
  // real box (it changes with the device preview and with the section's own settings) and hang the toolbar
  // just BELOW the section instead, so the section being edited stays fully reachable.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [toolbarBelow, setToolbarBelow] = useState(false);
  const setSortableRef = sortable.setNodeRef;
  const setWrapRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapRef.current = node;
      setSortableRef(node);
    },
    [setSortableRef],
  );
  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node || !selected) return;
    const measure = () => setToolbarBelow(node.getBoundingClientRect().height < SHORT_SECTION_PX);
    measure();
    // Guarded so the component stays inert under jsdom / any non-browser host (mirrors CanvasFrame).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(node);
    return () => ro?.disconnect();
  }, [selected]);

  // A section can momentarily be missing mid-edit (removal); render nothing rather than crash.
  const hydrated = useHydratedSection(section ?? EMPTY_SECTION);
  if (!section || !context) return null;

  const label = sectionDisplayName(section);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : 1,
  };

  // PART SELECTION — select the SMALLEST editable thing under the pointer instead of the whole section.
  // Two ways in (both route here on the canvas capture phase, so we win the race with EditableSection's
  // bubble-phase section-select and can `stopPropagation`):
  //   • Select mode ON (Shopify-style toolbar toggle): any click picks the innermost part.
  //   • This section already SELECTED: a click on one of its parts DRILLS IN — into a field hotspot OR a
  //     child block (1st click selected the section, the 2nd refines to the heading/button/…). A click on
  //     non-part chrome resolves to the section and falls through to the normal section select — which
  //     also clears the drill.
  //
  // WHICH part is decided by the shared `resolveCanvasTarget` — the same resolver <HoverFrame> uses to
  // draw its dashed outline, so the thing you saw framed on hover is the thing this click selects.
  // Order matters below: `selectSection` clears the block + focused field, so `selectBlock`/`focusField`
  // MUST run after it.
  function onCanvasClickCapture(e: React.MouseEvent) {
    if (!selectMode && !selected) return;
    const t = resolveCanvasTarget(e.target as HTMLElement, {
      selectMode,
      selectedSectionId: selected ? id : null,
    });
    if (!t || t.kind === 'section') return; // plain section click → EditableSection's own onSelect

    e.stopPropagation();
    selectSection(t.sectionId);
    if (t.kind === 'field') {
      if (t.blockId) selectBlock(t.blockId);
      focusField(t.id);
      return;
    }
    selectBlock(t.id);
  }

  // A HIDDEN section shows NOTHING in the canvas — the eye means "I don't want to see it" (the founder's
  // rule, same as hidden blocks): no content, no dimmed ghost, no placeholder marker. It disappears exactly
  // like the live storefront / Preview. We still mount an EMPTY zero-height node so dnd-kit keeps a real DOM
  // node for this `order[]` id (a bare `return null` would break reordering of the OTHER sections).
  // Visibility is managed from the left rail, which lists hidden sections with a Show control.
  if (hidden) {
    return (
      <div ref={sortable.setNodeRef} aria-hidden="true" style={{ height: 0, overflow: 'hidden' }} />
    );
  }

  return (
    <div
      ref={setWrapRef}
      style={style}
      className="bld-canvas-secwrap"
      data-aqx-opener={openerId === id ? '' : undefined}
      data-select-mode={selectMode || undefined}
      data-selected={selected || undefined}
      onClickCapture={onCanvasClickCapture}
    >
      <EditableSection
        section={hydrated}
        index={index}
        selected={selected}
        onSelect={selectSection}
        dragHandle={{ ...sortable.attributes, ...sortable.listeners }}
      >
        <SectionInner section={hydrated} context={context} />
      </EditableSection>
      {/* Floating section toolbar (build target 8) — a presentational pill that appears above the
          SELECTED section in the canvas. Wired only to existing engine actions (hide/show, remove);
          "Ask for changes" is a decorative AI affordance. NOT shown while dragging. */}
      {selected && !sortable.isDragging && (
        <div
          className={`bld-sectoolbar${toolbarBelow ? ' bld-sectoolbar--below' : ''}`}
          role="toolbar"
          aria-label={btp('chrome.rail.actions_aria', '{name} actions', { name: label })}
        >
          <button
            type="button"
            className="bld-sectoolbar__btn"
            aria-label={
              hidden
                ? btp('chrome.sec.show_named', 'Show {name}', { name: label })
                : btp('chrome.sec.hide_named', 'Hide {name}', { name: label })
            }
            aria-pressed={hidden}
            disabled={readOnly}
            title={
              readOnly
                ? bt('chrome.view.readonly', 'Read-only consultation')
                : hidden
                  ? bt('chrome.sec.show', 'Show section')
                  : bt('chrome.sec.hide', 'Hide section')
            }
            onClick={() => toggleSectionDisabled(id)}
          >
            {hidden ? <EyeOffIcon size={15} /> : <EyeIcon size={15} />}
          </button>
          <button
            type="button"
            className="bld-sectoolbar__btn bld-sectoolbar__btn--danger"
            aria-label={btp('chrome.sec.remove_named', 'Remove {name}', { name: label })}
            disabled={readOnly}
            title={
              readOnly
                ? bt('chrome.view.readonly', 'Read-only consultation')
                : bt('chrome.sec.remove', 'Remove section')
            }
            onClick={() => removeSection(id)}
          >
            <TrashIcon size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Below this box height a section cannot host the floating toolbar inset at its top-right without
 * covering its own content — it hangs under the section instead. Sized above the tallest bar-like
 * section (a header with an announcement strip) and far below any real content section.
 */
const SHORT_SECTION_PX = 96;

/** Stable empty placeholder so the hydration hook keeps a constant dependency arity. */
const EMPTY_SECTION: SectionInstance = { id: '', type: '', version: 0, settings: {}, blocks: [] };
